import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DetailSection } from '../../shell/DetailSection.js';
import {
  customerDisplayName,
  deliveryAddressText,
  formatAllergenLabel,
  formatFulfilmentMode,
  formatMerchantDateTime,
  formatOrderStatus,
  formatOrderValue,
  formatTimelineLabel,
  formatTimelineActor,
  historyOccurredAt,
  isDeliveryFulfilment,
  isIncomingOrder,
  isMerchantHistoryStatus,
  lineDisplayName,
  orderAddressVisible,
  settlementLooksUnrecorded,
  shortOrderLabel,
  type OrderDetail,
  type OrderLine,
  type OrderMoney,
  type OrderSummary,
  type OrderTimelineEvent,
} from './orderBoardApi.js';
import {
  courierStatusPath,
  incomingBoardPath,
  printTicketPath,
  rejectOrderPath,
  returnedOrderPath,
} from './orderBoardState.js';

function orderStatusTone(status?: string | null): string {
  if (!status) return 'neutral';
  if (isIncomingOrder({ status })) return 'incoming';
  if (status === 'PREPARING' || status === 'ACCEPTED') return 'prep';
  if (status === 'READY' || status === 'COLLECTED' || status === 'DELIVERED') return 'ready';
  if (['CANCELLED', 'REJECTED', 'EXPIRED', 'PAYMENT_FAILED', 'DELIVERY_FAILED'].includes(status)) {
    return 'blocked';
  }
  if (status === 'IN_TRANSIT' || status === 'AWAITING_COURIER') return 'out';
  return 'neutral';
}

function lineRequiresWeight(line: OrderLine): boolean {
  return (line as OrderLine & { requiresWeight?: boolean | null }).requiresWeight === true;
}

function orderOutcomeLabel(order: Pick<OrderDetail, 'status' | 'cancelledAt'>): string | null {
  if (order.cancelledAt) return 'Cancelled';
  if (/COLLECT/.test(order.status ?? '')) return 'Collected';
  if (/DELIVER/.test(order.status ?? '')) return 'Delivered';
  if (order.status && isMerchantHistoryStatus(order.status)) return 'Completed';
  return null;
}

function moneyHasValue(money?: OrderMoney | null): boolean {
  return (money?.minor ?? 0) !== 0;
}

export function orderPageTitle(order?: OrderSummary | null): string {
  const ref = order?.publicReference?.trim();
  if (ref) return ref;
  return order?.id ?? 'Order';
}

export function OrderStatusBadge({ status }: { status?: string | null }) {
  const tone = orderStatusTone(status);
  return (
    <span className={`mer-order-status-badge mer-order-status-${tone}`}>
      {formatOrderStatus(status)}
    </span>
  );
}

export function RealtimeLiveMark({ live, compact = false }: { live: boolean; compact?: boolean }) {
  return (
    <span
      className={`m08-live-mark${live ? ' m08-live-mark-on' : ''}${compact ? ' m08-live-mark-compact' : ''}`}
      title={live ? 'Live sockets connected' : 'Polling for updates'}
      data-testid="merchant-realtime-mark"
      data-live={live ? 'sockets' : 'poll'}
      aria-label={live ? 'Live sockets' : 'Polling'}
    >
      {live ? (compact ? 'Live' : '● Live sockets') : compact ? 'Polling' : '○ Polling'}
    </span>
  );
}

export function OrderLineList({
  order,
  onSnoozeLine,
}: {
  order: OrderDetail;
  onSnoozeLine?: (line: OrderLine) => void;
}) {
  if (!order.lines?.length) {
    return <p className="text-muted m-0">No line items on this order yet.</p>;
  }
  const itemsTotal =
    formatOrderValue(
      order.merchantSettlement?.originalMerchandiseSubtotal?.minor ??
        order.lines.reduce((sum, line) => sum + (line.lineTotal?.minor ?? 0), 0),
      order.merchantSettlement?.originalMerchandiseSubtotal?.currency ??
        order.lines[0]?.lineTotal?.currency ??
        'GBP',
    ) || null;

  return (
    <ul className="mer-detail-line-list">
      {order.lines.map((line, idx) => {
        const allergens = (line.allergens ?? []).map(formatAllergenLabel).filter(Boolean);
        const lineTotal = formatOrderValue(
          line.lineTotal?.minor,
          line.lineTotal?.currency ?? 'GBP',
        );
        return (
          <li key={line.id ?? `${lineDisplayName(line)}-${idx}`} className="m08-line-row">
            <span className="mer-detail-line-qty">{line.quantity ?? 1}×</span>
            <div className="m08-line-body">
              <div className="m08-line-main">
                <span className="mer-detail-line-name">{lineDisplayName(line)}</span>
                {lineTotal ? <span className="m08-line-total">{lineTotal}</span> : null}
              </div>
              {line.modifiers?.length ? (
                <ul className="m08-modifier-list">
                  {line.modifiers.map((mod) => (
                    <li key={mod.name}>{mod.name}</li>
                  ))}
                </ul>
              ) : null}
              {allergens.length || line.isAgeRestricted ? (
                <div className="m08-line-flags">
                  {line.isAgeRestricted ? (
                    <span className="m08-age-flag">Age restricted</span>
                  ) : null}
                  {allergens.length ? (
                    <div className="m08-allergen-block">
                      <p className="m08-allergen-caption m-0">Contains</p>
                      <ul className="m08-allergen-chips">
                        {allergens.map((label) => (
                          <li key={label}>{label}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {lineRequiresWeight(line) ? (
                <p className="m-0 text-body-sm text-muted">Requires a weight</p>
              ) : null}
              {line.notes ? <p className="m-0 text-body-sm">{line.notes}</p> : null}
            </div>
            {onSnoozeLine && (line.productId || line.stockItemId) ? (
              <button
                type="button"
                className="mer-detail-action m08-line-snooze"
                data-testid="merchant-order-line-snooze"
                onClick={() => onSnoozeLine(line)}
              >
                Snooze
              </button>
            ) : null}
          </li>
        );
      })}
      {itemsTotal ? (
        <li className="m08-line-foot">
          <span>Items total</span>
          <span className="m08-line-total">{itemsTotal}</span>
        </li>
      ) : null}
    </ul>
  );
}

function OrderCustomerMeta({
  order,
  hidePromised = false,
}: {
  order: OrderDetail;
  hidePromised?: boolean;
}) {
  const accepted = !isIncomingOrder(order);
  const address = deliveryAddressText(order);
  const name = customerDisplayName(order, accepted);
  const showAddress = isDeliveryFulfilment(order.fulfilmentMode);
  const addressVisible = orderAddressVisible(order);
  const occurred = formatMerchantDateTime(historyOccurredAt(order));
  const occurredLabel = orderOutcomeLabel(order);

  return (
    <dl className="mer-detail-meta-grid">
      <dt>Customer</dt>
      <dd data-testid="merchant-order-customer">{name}</dd>
      {order.fulfilmentMode ? (
        <>
          <dt>Fulfilment</dt>
          <dd>{formatFulfilmentMode(order.fulfilmentMode)}</dd>
        </>
      ) : null}
      {!hidePromised && order.promisedAt ? (
        <>
          <dt>Promised</dt>
          <dd>{formatMerchantDateTime(order.promisedAt) ?? '—'}</dd>
        </>
      ) : null}
      {occurred && hidePromised && occurredLabel ? (
        <>
          <dt>{occurredLabel}</dt>
          <dd>{occurred}</dd>
        </>
      ) : null}
      {order.itemCount != null ? (
        <>
          <dt>Items</dt>
          <dd>{order.itemCount}</dd>
        </>
      ) : null}
      {showAddress ? (
        <>
          <dt>Address</dt>
          <dd data-testid="merchant-order-address" aria-live="polite">
            {addressVisible
              ? address || 'No delivery address on this order'
              : 'Address is shown after you accept this order'}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

export function OrderDetailSummary({
  order,
  compact = false,
  onSnoozeLine,
}: {
  order: OrderDetail;
  compact?: boolean;
  onSnoozeLine?: (line: OrderLine) => void;
}) {
  return (
    <DetailSection testId="merchant-order-summary">
      <div className="mer-detail-summary">
        <div className="mer-detail-summary-head">
          <span className="mer-detail-ref">{order.publicReference ?? order.id}</span>
          {order.status ? <OrderStatusBadge status={order.status} /> : null}
        </div>
        <OrderCustomerMeta order={order} />
        {!compact ? <OrderLineList order={order} onSnoozeLine={onSnoozeLine} /> : null}
      </div>
    </DetailSection>
  );
}

function settlementMoney(money?: OrderMoney | null): string {
  if (!money) return '—';
  return formatOrderValue(money.minor, money.currency) ?? '—';
}

function commissionRate(rateBps: number | null): string {
  if (rateBps == null) return 'Not available';
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(rateBps / 100)}%`;
}

export function OrderSettlementCard({ order }: { order: OrderDetail }) {
  const settlement = order.merchantSettlement;
  if (!settlement) return null;

  const breakdown = order.priceBreakdown;
  const deliveryOrder = isDeliveryFulfilment(order.fulfilmentMode);
  const includedItemVat = settlement.includedMerchandiseVat ?? breakdown?.tax;
  const unrecorded = settlementLooksUnrecorded(order);
  const youReceive = unrecorded
    ? settlement.originalMerchandiseSubtotal
    : settlement.merchantEntitlement;
  const deliveryFee = breakdown?.deliveryFee;
  const adminFee = breakdown?.adminFee ?? breakdown?.serviceFee;
  const showDeliveryFee = deliveryOrder && moneyHasValue(deliveryFee);
  const showAdminFee = moneyHasValue(adminFee);
  const showCustomerFees = Boolean(breakdown && (showDeliveryFee || showAdminFee));
  const showVat = moneyHasValue(includedItemVat);

  return (
    <section
      className="mer-detail-section"
      aria-labelledby="merchant-order-settlement-title"
      data-testid="merchant-order-settlement"
    >
      <h2 id="merchant-order-settlement-title" className="mer-detail-section-title">
        Your settlement
      </h2>
      <dl className="mer-detail-meta-grid">
        <dt>Original items</dt>
        <dd>{settlementMoney(settlement.originalMerchandiseSubtotal)}</dd>
        {unrecorded ? null : (
          <>
            <dt>BeatQ-funded customer discount</dt>
            <dd>{settlementMoney(settlement.platformFundedDiscount)}</dd>
            <dt>Commission rate</dt>
            <dd>{commissionRate(settlement.commissionRateBps)}</dd>
            <dt>Commission</dt>
            <dd>{settlementMoney(settlement.commission)}</dd>
          </>
        )}
      </dl>
      <p className="m08-you-receive" data-testid="merchant-order-you-receive">
        <span>You receive</span>
        <strong>{settlementMoney(youReceive)}</strong>
      </p>
      <p className="text-body-sm text-muted">
        {unrecorded
          ? 'Commission was not recorded on this ticket, so this is the original item total.'
          : 'The customer discount is funded by BeatQ; it does not reduce your original sales base.'}
      </p>
      {showCustomerFees ? (
        <>
          <h3 className="mer-detail-section-title">
            {deliveryOrder ? 'BeatQ Del customer fees' : 'Customer fees'}
          </h3>
          <dl className="mer-detail-meta-grid">
            {showDeliveryFee ? (
              <>
                <dt>BeatQ Del delivery fee</dt>
                <dd>{settlementMoney(deliveryFee)}</dd>
              </>
            ) : null}
            {showAdminFee ? (
              <>
                <dt>Admin fee</dt>
                <dd>{settlementMoney(adminFee)}</dd>
              </>
            ) : null}
          </dl>
          <p className="text-body-sm text-muted">
            These are customer charges and are not merchant revenue.
          </p>
        </>
      ) : null}
      {showVat ? (
        <p className="text-body-sm text-muted" data-testid="merchant-order-vat-note">
          VAT included in items: {settlementMoney(includedItemVat)}. This is accounting information
          already included in menu prices; it is not added to any total.
        </p>
      ) : null}
    </section>
  );
}

export function OrderFollowBody({
  order,
  live,
  notes,
  timeline,
  onSnoozeLine,
}: {
  order: OrderDetail;
  live: boolean;
  notes?: ReactNode;
  timeline: OrderTimelineEvent[];
  onSnoozeLine?: (line: OrderLine) => void;
}) {
  const promised = formatMerchantDateTime(order.promisedAt);
  const occurred = formatMerchantDateTime(historyOccurredAt(order));
  const occurredLabel = occurred ? orderOutcomeLabel(order) : null;
  const accepted = !isIncomingOrder(order);
  const customerName = customerDisplayName(order, accepted);

  return (
    <>
      <header className="m08-follow-head" data-testid="merchant-order-follow-head">
        {order.status ? <OrderStatusBadge status={order.status} /> : null}
        {order.fulfilmentMode ? (
          <span className="m08-follow-chip">{formatFulfilmentMode(order.fulfilmentMode)}</span>
        ) : null}
        {customerName ? <span className="m08-follow-chip">{customerName}</span> : null}
        {promised ? <span className="m08-follow-timing">Promised {promised}</span> : null}
        {occurred && occurredLabel ? (
          <span className="m08-follow-timing">
            {occurredLabel} {occurred}
          </span>
        ) : null}
        <RealtimeLiveMark live={live} />
      </header>
      <div className="m08-follow-layout" data-testid="merchant-order-follow-layout">
        <div className="m08-follow-main">
          <section
            className="mer-detail-section m08-follow-items"
            aria-labelledby="m08-follow-items"
          >
            <h2 id="m08-follow-items" className="mer-detail-section-title">
              Items
            </h2>
            <OrderLineList order={order} onSnoozeLine={onSnoozeLine} />
          </section>
          <OrderTimelineSection timeline={timeline} />
        </div>
        <aside className="m08-follow-side">
          <section className="mer-detail-section" data-testid="merchant-order-summary">
            <h2 className="mer-detail-section-title">Ticket</h2>
            <OrderCustomerMeta order={order} hidePromised />
          </section>
          <OrderSettlementCard order={order} />
          {notes}
        </aside>
      </div>
    </>
  );
}

export function KitchenTicketPreview({
  order,
  reprint = false,
  testId = 'merchant-print-modal-body',
}: {
  order: OrderSummary;
  reprint?: boolean;
  testId?: string;
}) {
  const detail = order as OrderDetail;
  const lines = detail.lines;
  const firstName = order.customerFirstName?.trim() || detail.customer?.firstName?.trim();

  return (
    <article className="m08-ticket-preview" data-testid={testId}>
      {reprint ? (
        <p className="m-0 m08-reprint-banner">REPRINT · {new Date().toLocaleTimeString()}</p>
      ) : null}
      <p className="m08-ticket-ref m-0">{shortOrderLabel(order)}</p>
      <p className="m-0 text-body-sm">
        {formatFulfilmentMode(order.fulfilmentMode)}
        {order.promisedAt ? ` · ${new Date(order.promisedAt).toLocaleTimeString()}` : ''}
      </p>
      {firstName ? <p className="m-0 text-body-sm">{firstName}</p> : null}
      {lines?.length ? (
        <ul className="mer-detail-line-list">
          {lines.map((line, idx) => (
            <li key={line.id ?? idx}>
              {line.quantity ?? 1}× {lineDisplayName(line)}
              {line.modifiers?.map((mod) => (
                <div key={mod.name} className="m08-modifier-list">
                  {mod.name}
                </div>
              ))}
              {line.allergens?.length ? (
                <span className="m08-allergen-flag"> {line.allergens.join(', ')}</span>
              ) : null}
              {line.isAgeRestricted ? (
                <span className="m08-allergen-flag"> Age restricted</span>
              ) : null}
              {lineRequiresWeight(line) ? (
                <span className="text-body-sm"> · requires a weight</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-body-sm text-muted m-0">
          Ticket for <strong>{shortOrderLabel(order)}</strong>
        </p>
      )}
    </article>
  );
}

export function OrderActionBar({
  orderId,
  status,
  fulfilmentMode,
  supportHref,
  onCancel,
  onAdjust,
  onPrint,
  onAdvance,
  onSnooze,
  advanceLabel,
  advanceBusy,
}: {
  orderId: string;
  status?: string | null;
  fulfilmentMode?: string | null;
  supportHref?: string;
  onCancel?: () => void;
  onAdjust?: () => void;
  onPrint?: () => void;
  onAdvance?: () => void;
  onSnooze?: () => void;
  advanceLabel?: string;
  advanceBusy?: boolean;
}) {
  const incoming = isIncomingOrder({ status: status ?? undefined });
  const terminal = isMerchantHistoryStatus(status);
  const returned = status != null && /RETURN|FAILED_HANDOVER|DELIVERY_FAILED/.test(status);
  const showCourier = isDeliveryFulfilment(fulfilmentMode) && advanceLabel !== 'BeatQ Del';
  const printControl = onPrint ? (
    <button
      type="button"
      className="mer-detail-action"
      data-testid="merchant-order-print"
      onClick={onPrint}
    >
      Print
    </button>
  ) : (
    <Link className="mer-detail-action" to={printTicketPath(orderId)}>
      Print ticket
    </Link>
  );

  return (
    <footer
      className="m08-follow-bar"
      data-testid="merchant-order-actions"
      id="merchant-order-actions"
    >
      {terminal ? (
        <div className="m08-follow-bar-secondary m08-follow-bar-terminal">
          <p className="m08-follow-outcome m-0">
            This order was {formatOrderStatus(status)}
            {returned ? (
              <>
                {' · '}
                <Link className="mer-link-action" to={returnedOrderPath(orderId)}>
                  Returned order
                </Link>
              </>
            ) : null}
          </p>
          {printControl}
        </div>
      ) : (
        <>
          <div className="m08-follow-bar-primary">
            {incoming ? (
              <Link
                className="mer-detail-action mer-detail-action-primary"
                to={incomingBoardPath(orderId)}
              >
                Accept
              </Link>
            ) : null}
            {!incoming && onAdvance && advanceLabel ? (
              <button
                type="button"
                className="mer-detail-action mer-detail-action-primary"
                data-testid="merchant-order-advance"
                disabled={advanceBusy}
                onClick={onAdvance}
              >
                {advanceLabel}
              </button>
            ) : null}
          </div>
          <div className="m08-follow-bar-secondary">
            {incoming ? (
              <Link
                className="mer-detail-action m08-reject-subordinate"
                to={rejectOrderPath(orderId)}
              >
                Reject
              </Link>
            ) : null}
            {!incoming && onAdjust ? (
              <button
                type="button"
                className="mer-detail-action"
                data-testid="merchant-order-adjust"
                onClick={onAdjust}
              >
                Adjust time
              </button>
            ) : null}
            {printControl}
            <a className="mer-detail-action" href="#orderNote">
              Add note
            </a>
          </div>
          <details className="m08-follow-more">
            <summary>More</summary>
            <div className="m08-follow-bar-tertiary">
              {!incoming && onCancel ? (
                <button
                  type="button"
                  className="mer-detail-action mer-detail-action-danger"
                  data-testid="merchant-order-cancel"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              ) : null}
              {showCourier ? (
                <Link className="mer-detail-action" to={courierStatusPath(orderId)}>
                  BeatQ Del
                </Link>
              ) : null}
              {onSnooze ? (
                <button
                  type="button"
                  className="mer-detail-action"
                  data-testid="merchant-order-snooze"
                  onClick={onSnooze}
                >
                  Snooze item
                </button>
              ) : null}
              {supportHref ? (
                <a className="mer-detail-action" href={supportHref}>
                  Contact support
                </a>
              ) : null}
              {returned ? (
                <Link className="mer-detail-action" to={returnedOrderPath(orderId)}>
                  Returned order
                </Link>
              ) : null}
            </div>
          </details>
        </>
      )}
    </footer>
  );
}

export function OrderTimelineSection({ timeline }: { timeline: OrderTimelineEvent[] }) {
  if (!timeline.length) {
    return (
      <section
        className="mer-detail-section"
        aria-labelledby="m08-follow-timeline"
        data-testid="merchant-order-timeline"
      >
        <h2 id="m08-follow-timeline" className="mer-detail-section-title">
          Timeline
        </h2>
        <p className="m-0 text-body-sm text-muted">No timeline events on this order yet.</p>
      </section>
    );
  }
  return (
    <section
      className="mer-detail-section"
      aria-labelledby="m08-follow-timeline"
      data-testid="merchant-order-timeline"
    >
      <h2 id="m08-follow-timeline" className="mer-detail-section-title">
        Timeline
      </h2>
      <ol className="mer-detail-timeline">
        {timeline.map((event) => {
          const actor = formatTimelineActor(event.actor);
          return (
            <li key={event.id ?? `${event.label}-${event.occurredAt}`}>
              <span className="mer-detail-timeline-label">
                {formatTimelineLabel(event)}
                {actor ? ` · ${actor}` : ''}
              </span>
              {event.occurredAt ? (
                <time className="mer-detail-timeline-time" dateTime={event.occurredAt}>
                  {formatMerchantDateTime(event.occurredAt) ?? event.occurredAt}
                </time>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export { DetailFooterNav } from '../../shell/DetailUi.js';
